import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams, Link } from 'react-router-dom';
import projects from './projectsData';
import ProjectLightbox from './ProjectLightbox';
import CaseStudy from './CaseStudy';
import './Projects.scss';

const ProjectDetail = () => {
    const { slug } = useParams();
    const location = useLocation();
    const project = projects.find((p) => p.slug === slug);
    const [lightboxOpen, setLightboxOpen] = useState(-1);
    const [currentImageIndex, setCurrentImageIndex] = useState(-1);
    const galleryRef = useRef(null);

    useEffect(() => {
        if (project) {
            document.title = `${project.title} – Kayleigh Schlupp`;
        } else {
            document.title = 'Project Not Found – Kayleigh Schlupp';
        }
    }, [project]);

    // Handle URL hash linking (e.g. /projects/:slug#fig-2-0)
    useEffect(() => {
        if (!project || !location || !location.hash) return;
        const id = location.hash.replace('#', '');
        const idx = project.gallery ? project.gallery.findIndex((g) => g.id === id) : -1;
        if (idx !== -1) {
            const el = document.getElementById(id);
            if (el && el.scrollIntoView) {
                // Respect prefers-reduced-motion
                const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                el.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'center' });
                // open lightbox shortly after scrolling (or immediately if reduced motion)
                const delay = prefersReduced ? 100 : 450;
                setTimeout(() => {
                    setLightboxOpen(idx);
                    setCurrentImageIndex(idx);
                }, delay);
            } else {
                setLightboxOpen(idx);
                setCurrentImageIndex(idx);
            }
        }
    }, [location, project]);

    // Update URL when user navigates through gallery
    useEffect(() => {
        if (lightboxOpen !== -1 && currentImageIndex !== -1 && project?.gallery) {
            const imageId = project.gallery[currentImageIndex]?.id;
            if (imageId) {
                window.history.replaceState(null, '', `${window.location.pathname}#${imageId}`);
            }
        }
    }, [currentImageIndex, lightboxOpen, project]);

    useEffect(() => {
        const resizeGridItem = (item) => {
            const grid = galleryRef.current;
            if (!grid || window.innerWidth < 1024) return;
            const rowHeight = 10; // matches grid-auto-rows
            const rowGap = 10; // matches CSS gap
            const img = item.querySelector('img');
            if (img && img.complete) {
                const contentHeight = img.getBoundingClientRect().height;
                const rowSpan = Math.ceil((contentHeight + rowGap) / (rowHeight + rowGap));
                item.style.gridRowEnd = `span ${rowSpan}`;
            }
        };

        const resizeAllGridItems = () => {
            if (galleryRef.current && window.innerWidth >= 1024) {
                const allItems = galleryRef.current.querySelectorAll('.project-detail__thumb');
                allItems.forEach(resizeGridItem);
            }
        };

        if (project && project.gallery && galleryRef.current) {
            const images = galleryRef.current.querySelectorAll('img');
            images.forEach((img) => {
                if (img.complete) {
                    resizeGridItem(img.closest('.project-detail__thumb'));
                } else {
                    img.addEventListener('load', () => {
                        resizeGridItem(img.closest('.project-detail__thumb'));
                    });
                }
            });
            window.addEventListener('resize', resizeAllGridItems);
            return () => window.removeEventListener('resize', resizeAllGridItems);
        }
    }, [project]);

    if (!project) {
        return (
            <section id="project-detail" className="section-wrapper page">
                <div className="section-inner">
                    <h1 className="page-title">Project not found</h1>
                    <p>The project you requested was not found.</p>
                    <Link to="/projects">Back to projects</Link>
                </div>
            </section>
        );
    }

    return (
        <section id="project-detail" className="section-wrapper page">
            <div className="section-inner">
                <nav aria-label="Breadcrumb" className="breadcrumbs">
                    <ol>
                        <li><Link to="/">Home</Link></li>
                        <li><Link to="/projects">Projects</Link></li>
                        <li aria-current="page">{project.title}</li>
                    </ol>
                </nav>
                <h1 className="page-title">{project.title}</h1>

                {(project.liveUrl || project.repoUrl) && (
                    <div className="project-detail__links">
                        {project.liveUrl && (
                            <a
                                href={project.liveUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary"
                                aria-label={`Open live site for ${project.title} in a new tab`}
                            >
                                <FontAwesomeIcon icon={["fas", "up-right-from-square"]} />
                                <span>View live site</span>
                            </a>
                        )}
                        {project.repoUrl && (
                            <a
                                href={project.repoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary"
                                aria-label={`Open code repository for ${project.title} in a new tab`}
                            >
                                <FontAwesomeIcon icon={["fab", "github"]} />
                                <span>View code</span>
                            </a>
                        )}
                    </div>
                )}
                {project.caseStudyUrl && (
                    <>
                        <CaseStudy caseStudyUrl={project.caseStudyUrl} />
                    </>
                )}

                {project.gallery && project.gallery.length > 0 && (
                    <>
                        <h2 className='project-detail__gallery-title'>Gallery</h2>
                        <div
                            ref={galleryRef}
                            className="project-detail__gallery"
                            data-count={project.gallery.length}
                            data-columns={project.gallery.length <= 2 ? 1 : project.gallery.length <= 4 ? 2 : project.gallery.length <= 8 ? 3 : 4}
                        >
                            {project.gallery.map((img, i) => (
                                <figure key={i} id={img.id ? img.id : undefined} className="project-detail__thumb">
                                    <figcaption className="project-detail__caption sr-only">{img.caption}</figcaption>
                                    <button
                                        type="button"
                                        className="project-detail__thumb-button"
                                        onClick={() => {
                                            setLightboxOpen(i);
                                            setCurrentImageIndex(i);
                                        }}
                                        onFocus={() => {
                                            const prefetchImage = new Image();
                                            prefetchImage.src = img.src1200;
                                        }}
                                        aria-label={`Open image ${i + 1} of ${project.gallery.length}: ${img.caption}`}
                                        aria-describedby={img.id ? img.id : undefined}
                                    >
                                        <img
                                            src={img.src400}
                                            srcSet={`${img.src400} 400w, ${img.src800} 800w, ${img.src1200} 1200w`}
                                            sizes="(max-width: 600px) 100vw, 200px"
                                            alt=""
                                            loading="lazy"
                                            onMouseEnter={() => {
                                                const prefetchImage = new Image();
                                                prefetchImage.src = img.src1200;
                                            }}
                                            aria-hidden="true"
                                        />
                                    </button>
                                </figure>
                            ))}
                        </div>
                    </>
                )}

                <div className="project-detail__back">
                    <Link to="/projects" className="btn btn-link">← Back to projects</Link>
                </div>

                {lightboxOpen !== -1 && (
                    <ProjectLightbox
                        items={project.gallery}
                        startIndex={lightboxOpen}
                        onImageChange={setCurrentImageIndex}
                        onClose={() => {
                            setLightboxOpen(-1);
                            setCurrentImageIndex(-1);
                            // Remove hash from URL when closing lightbox
                            window.history.replaceState(null, '', window.location.pathname);
                        }}
                    />
                )}
            </div>
        </section>
    );
};

export default ProjectDetail;
